import { createContext } from '@hover/blueprint';
import type { PropsWithChildren } from 'react';
import React from 'react';

import { usePublicOrgInfoQuery } from 'src/shared/graphql/generated';

import type {
  CustomizationCategory,
  MaterialRegionOrPaint,
} from '../../constants/customizationCategories';
import type { PartnerMetadata } from '../../constants/partners';
import { DesignerPartner, NO_PARTNER_METADATA } from '../../constants/partners';
import type { AiDisplayImageFragment } from '../../graphql/generated';
import {
  useAiDisplayImageProjectPartnerQuery,
  useProductCatalogCustomizeCategoriesQuery,
  useUserOrgsQuery,
} from '../../graphql/generated';
import type {
  AiOriginalImageWithPartnerInfo,
  ProjectWithPartnerInfo,
} from '../../helpers/partners';
import {
  getPartnerForImage,
  getPartnerForOriginalImage,
  getPartnerForProject,
  publicOrgInfoToPartnerMetadata,
} from '../../helpers/partners';

export interface DesignerPartnerContext {
  isLoading: boolean;
  metadata: PartnerMetadata | null;
}

const [Provider, useContext] = createContext<
  DesignerPartnerContext | undefined
>({
  name: 'DesignerPartnerContext',
  strict: false,
});

//
// Public hooks
//

/**
 * Access the current partner association with loading state. This returns the
 * `DesignerPartner` enum which can be used to access other hard-coded partner
 * configuration.
 *
 * - If you need the partner cobranding label and logo, use
 *   `usePartnerCobranding`
 * - If you need the hard-coded partner configuration from
 *   `PARTNER_CONFIGURATION_MAP`, use `usePartnerConfiguration`
 *
 * NOTE: this requires the `DesignerPartnerContext` to be provided by a parent.
 * Because the partner association is accessed in a few different ways in our
 * data model, different providers must be used for different data contexts:
 *
 * @see {@link PartnerImageProvider} in relation to an `AiDisplayImage`
 * @see {@link PartnerProjectProvider} in relation to a `Project`
 * @see {@link PartnerOriginalImageProvider} in relation to a `AiOriginalImage`
 */
export const usePartner = () => {
  const context = useContext();

  if (!context) {
    return {
      partner: null,
      isLoading: false,
    };
  }

  const { isLoading, metadata } = context;

  return {
    isLoading,
    partner: metadata?.partner,
  };
};

const categoryForBrandType = (
  brandType: MaterialRegionOrPaint | undefined,
): CustomizationCategory | null => {
  if (!brandType) return null;

  return brandType === 'paint'
    ? { type: 'paint', region: 'walls' }
    : {
        type: 'material',
        region: brandType,
      };
};

/**
 * Get the cobranding information for the current associated partner (if any).
 *
 * This hook returns `null` while loading the data necessary to determine the
 * partner and when there is no partner association. Use `isLoading` from
 * `usePartner` if you need to access the loading state.
 */
export const usePartnerCobranding = () => {
  const context = useContext();

  if (!context || !context.metadata) return null;

  const {
    displayName,
    logoUrl,
    logoUrlInvert,
    configuration: { brandType },
    orgId,
    partner,
  } = context.metadata;

  return {
    displayName,
    logoUrl,
    logoUrlInvert,
    type: brandType,
    orgId,
    partner,
    category: categoryForBrandType(brandType),
  };
};

/**
 * Get the hard-coded partner configuration from `PARTNER_CONFIGURATION_MAP` for
 * the current associated partner (if any).
 *
 * This hook returns `null` while loading the data necessary to determine the
 * partner and when there is no partner association. Use `isLoading` from
 * `usePartner` if you need to access the loading state.
 */
export const usePartnerConfiguration = () => {
  const context = useContext();

  if (!context || !context.metadata || context.isLoading) return null;

  return context.metadata?.configuration;
};

/**
 * Query Product Catalog categories for the partner context. If there is no
 * partner, query the default set of Product Catalog categories.
 *
 * This hook combines the loading state of the partner context and the categories
 * query.
 */
export const usePartnerCustomizeCategories = () => {
  const context = useContext();

  const orgId =
    context?.metadata?.partner !== DesignerPartner.NONE
      ? context?.metadata?.orgId
      : undefined;

  const skip = !context || !context.metadata || context.isLoading;
  const {
    data,
    loading: categoriesLoading,
    error,
  } = useProductCatalogCustomizeCategoriesQuery({
    variables: { ...(orgId ? { orgId: String(orgId) } : undefined) },
    skip,
  });

  const loading = categoriesLoading || (context?.isLoading ?? false);

  return { data: data?.genAiCategories.nodes, loading, error };
};

//
// Providers
//

interface UsePartnerOptions {
  skip?: boolean;
}

interface UseUserWithOrgsOptions extends UsePartnerOptions {}

const useUserWithOrgs = ({ skip }: UseUserWithOrgsOptions) => {
  const { data: userData, loading } = useUserOrgsQuery({
    skip,
  });

  return [userData?.profile.user, loading] as const;
};

interface UsePartnerForImageOptions extends UsePartnerOptions {
  aiDisplayImageId: AiDisplayImageFragment['id'];
}

const usePartnerForImage = ({
  aiDisplayImageId,
  skip,
}: UsePartnerForImageOptions): DesignerPartnerContext => {
  const { data: imageData, loading: isImageLoading } =
    useAiDisplayImageProjectPartnerQuery({
      variables: {
        id: aiDisplayImageId,
      },
      skip,
    });

  const [user, isUserLoading] = useUserWithOrgs({ skip });

  if (isImageLoading || isUserLoading) {
    return { metadata: NO_PARTNER_METADATA, isLoading: true };
  }

  const image = imageData?.aiDisplayImage;

  return {
    metadata: image ? getPartnerForImage(image, user) : NO_PARTNER_METADATA,
    isLoading: false,
  };
};

export interface PartnerImageProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForImageOptions {}

/**
 * Given an AI display image ID, fetch the partner associated with that image
 * and make it available via `usePartner`
 */
export const PartnerImageProvider = ({
  aiDisplayImageId,
  skip,
  ...props
}: PartnerImageProviderProps) => (
  <Provider value={usePartnerForImage({ aiDisplayImageId, skip })} {...props} />
);

interface UsePartnerForProjectOptions extends UsePartnerOptions {
  project: ProjectWithPartnerInfo | undefined | null;
}

const usePartnerForProject = ({
  project,
  skip,
}: UsePartnerForProjectOptions): DesignerPartnerContext => {
  const [user, isUserLoading] = useUserWithOrgs({ skip });

  if (isUserLoading) {
    return { metadata: NO_PARTNER_METADATA, isLoading: true };
  }

  return {
    metadata: project
      ? getPartnerForProject(project, user)
      : NO_PARTNER_METADATA,
    isLoading: false,
  };
};

export interface PartnerProjectProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForProjectOptions {}

/**
 * Given a project that includes `publicOrgInfo`, fetch the partner associated
 * with that project and make it available via `usePartner`
 */
export const PartnerProjectProvider = ({
  project,
  skip,
  ...props
}: PartnerProjectProviderProps) => (
  <Provider value={usePartnerForProject({ project, skip })} {...props} />
);

interface UsePartnerForOriginalImageOptions extends UsePartnerOptions {
  aiOriginalImage: AiOriginalImageWithPartnerInfo | null | undefined;
}

const usePartnerForOriginalImage = ({
  aiOriginalImage,
  skip,
}: UsePartnerForOriginalImageOptions): DesignerPartnerContext => {
  const [user, isUserLoading] = useUserWithOrgs({ skip });

  if (isUserLoading) {
    return { metadata: NO_PARTNER_METADATA, isLoading: true };
  }

  return {
    metadata: aiOriginalImage
      ? getPartnerForOriginalImage(aiOriginalImage, user)
      : NO_PARTNER_METADATA,
    isLoading: false,
  };
};

export interface PartnerOriginalImageProviderProps
  extends PropsWithChildren<{}>,
    UsePartnerForOriginalImageOptions {}

/**
 * Given an original image that includes `publicOrgInfo`, fetch the partner
 * associated with that original image and make it available via `usePartner`
 */
export const PartnerOriginalImageProvider = ({
  aiOriginalImage,
  skip,
  ...props
}: PartnerOriginalImageProviderProps) => (
  <Provider
    value={usePartnerForOriginalImage({ aiOriginalImage, skip })}
    {...props}
  />
);

interface UsePartnerForAffiliateOrgIdOptions extends UsePartnerOptions {
  affOrgId: string | null | undefined;
}

const usePartnerForAffiliateOrgId = ({
  affOrgId,
}: UsePartnerForAffiliateOrgIdOptions): DesignerPartnerContext => {
  const { data: publicOrgInfoData, loading: publicOrgInfoLoading } =
    usePublicOrgInfoQuery({
      variables: {
        publicIdentifier: affOrgId || '',
      },
      skip: !affOrgId,
    });

  return {
    metadata: publicOrgInfoToPartnerMetadata(publicOrgInfoData?.publicOrgInfo),
    isLoading: publicOrgInfoLoading,
  };
};

export interface PartnerAffiliateOrgIdProps
  extends PropsWithChildren<{}>,
    UsePartnerForAffiliateOrgIdOptions {}

/**
 * Given a public affiliate org id, fetch the partner
 * associated with that org id and make it available via `usePartner`
 */
export const PartnerAffiliateOrgIdProvider = ({
  affOrgId,
  ...props
}: PartnerAffiliateOrgIdProps) => (
  <Provider value={usePartnerForAffiliateOrgId({ affOrgId })} {...props} />
);
